import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, X, Calendar as CalendarIcon, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { CATEGORY_OPTIONS } from "@/lib/categories";
import { panelAccentDot } from "@/lib/panel-ui";
import {
  startOfToday,
  startOfTomorrow,
  endOfTomorrow,
  startOfWeek,
  endOfWeek,
  addWeeks,
  endOfDay,
  isSaturday,
  isSunday,
  nextSaturday,
  nextSunday,
  format,
} from "date-fns";
import { ptBR } from "date-fns/locale";

// ─── Types ───────────────────────────────────────────────

export interface EventFilters {
  category: string;
  datePreset: DatePreset;
  dateRange: DateRange | undefined;
  priceMin: string;
  priceMax: string;
  modality: "all" | "presential" | "online";
  sort: "date" | "relevance";
}

export const defaultEventFilters: EventFilters = {
  category: "",
  datePreset: null,
  dateRange: undefined,
  priceMin: "",
  priceMax: "",
  modality: "all",
  sort: "date",
};

type DatePreset = "hoje" | "amanha" | "semana" | "fim_de_semana" | "proxima_semana" | null;

const DATE_PRESET_VALUES: Exclude<DatePreset, null>[] = [
  "hoje",
  "amanha",
  "semana",
  "fim_de_semana",
  "proxima_semana",
];

export function getDateRangeFromPreset(preset: DatePreset): { start: Date; end: Date } | null {
  if (!preset) return null;
  const today = startOfToday();
  switch (preset) {
    case "hoje":
      return { start: today, end: endOfDay(today) };
    case "amanha":
      return { start: startOfTomorrow(), end: endOfTomorrow() };
    case "semana": {
      const ws = startOfWeek(today, { weekStartsOn: 0 });
      const we = endOfWeek(today, { weekStartsOn: 0 });
      return { start: today > ws ? today : ws, end: we };
    }
    case "fim_de_semana": {
      if (isSaturday(today)) {
        const sun = new Date(today);
        sun.setDate(sun.getDate() + 1);
        return { start: today, end: endOfDay(sun) };
      } else if (isSunday(today)) {
        return { start: today, end: endOfDay(today) };
      }
      return { start: nextSaturday(today), end: endOfDay(nextSunday(today)) };
    }
    case "proxima_semana": {
      const ns = startOfWeek(addWeeks(today, 1), { weekStartsOn: 0 });
      const ne = endOfWeek(addWeeks(today, 1), { weekStartsOn: 0 });
      return { start: ns, end: ne };
    }
    default:
      return null;
  }
}

// ─── Sub-components ──────────────────────────────────────

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-150 ease-out whitespace-nowrap active:scale-[0.97]",
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "bg-secondary/60 text-muted-foreground hover:bg-secondary/90 hover:text-foreground border border-border/40"
      )}
    >
      {label}
      {active && <X className="inline-block h-3 w-3 ml-1.5 -mr-0.5" />}
    </button>
  );
}

function FilterDropdown({
  label,
  active,
  activeLabel,
  onClear,
  children,
  align = "start",
  className,
}: {
  label: string;
  active: boolean;
  activeLabel?: string;
  onClear?: () => void;
  children: React.ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "group inline-flex items-center gap-1.5 h-9 px-3.5 py-1.5 rounded-full border text-xs sm:text-[13px] font-medium transition-all duration-150 ease-out whitespace-nowrap active:scale-[0.97] outline-none select-none",
            active
              ? "border-primary/40 bg-primary/10 text-primary ring-1 ring-primary/25"
              : "border-border/60 bg-card text-foreground/80 hover:bg-secondary/60 hover:border-border/90 hover:text-foreground",
            className
          )}
        >
          <span>{active && activeLabel ? activeLabel : label}</span>
          {active && onClear ? (
            <span
              role="button"
              tabIndex={0}
              className="p-0.5 -mr-1 rounded-full hover:bg-primary/20 text-primary transition-colors flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  onClear();
                }
              }}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : (
            <ChevronDown className="h-3.5 w-3.5 opacity-50 ml-0.5 transition-transform duration-150 group-data-[state=open]:rotate-180 shrink-0" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className={cn("rounded-[24px] border border-border/60 bg-card/95 backdrop-blur-xl shadow-xl p-4 sm:p-5 text-foreground", className)}
        sideOffset={8}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

// ─── Main Component ──────────────────────────────────────

interface Props {
  filters: EventFilters;
  onChange: (filters: EventFilters) => void;
}

export function EventFilterBar({ filters, onChange }: Props) {
  const { t } = useTranslation("landingEvents");
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);

  const datePresetLabels: Record<Exclude<DatePreset, null>, string> = {
    hoje: t('filterBar.datePresets.today'),
    amanha: t('filterBar.datePresets.tomorrow'),
    semana: t('filterBar.datePresets.thisWeek'),
    fim_de_semana: t('filterBar.datePresets.weekend'),
    proxima_semana: t('filterBar.datePresets.nextWeek'),
  };

  const update = (partial: Partial<EventFilters>) =>
    onChange({ ...filters, ...partial });

  const categoryLabel = CATEGORY_OPTIONS.find((c) => c.value === filters.category)?.label;

  // Date label
  const getDateLabel = (): string | undefined => {
    if (filters.datePreset) {
      return datePresetLabels[filters.datePreset];
    }
    if (filters.dateRange?.from) {
      if (filters.dateRange.to) {
        return `${format(filters.dateRange.from, "dd/MM")} – ${format(filters.dateRange.to, "dd/MM")}`;
      }
      return format(filters.dateRange.from, "dd/MM/yyyy");
    }
    return undefined;
  };

  const dateLabel = getDateLabel();
  const hasDateFilter = !!filters.datePreset || !!filters.dateRange?.from;
  const hasPriceFilter = !!filters.priceMin || !!filters.priceMax;

  const priceLabel = hasPriceFilter
    ? filters.priceMin && filters.priceMax
      ? `R$${filters.priceMin} – R$${filters.priceMax}`
      : filters.priceMin
        ? `${t('filterBar.price.fromPrefix')} R$${filters.priceMin}`
        : `${t('filterBar.price.upToPrefix')} R$${filters.priceMax}`
    : undefined;

  const modalityLabel =
    filters.modality === "presential" ? t('filterBar.modality.presential') :
    filters.modality === "online" ? t('filterBar.modality.online') : undefined;

  const activeCount = [
    !!filters.category,
    hasDateFilter,
    hasPriceFilter,
    filters.modality !== "all",
    filters.sort !== "date",
  ].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-2.5">
      {/* Filter bar */}
      <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <span className="text-xs font-medium text-muted-foreground/75 shrink-0 hidden sm:inline-flex items-center mr-0.5">
          {t('filterBar.filterByLabel')}
        </span>

        {/* Category */}
        <FilterDropdown
          label={t('filterBar.category.label')}
          active={!!filters.category}
          activeLabel={categoryLabel}
          onClear={() => update({ category: "" })}
        >
          <div className="space-y-3 min-w-[220px]">
            <p className="font-display text-sm font-semibold tracking-tight text-foreground flex items-center gap-2">
              <span className={panelAccentDot} />
              {t('filterBar.category.label')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORY_OPTIONS.filter(c => c.value !== "").map((cat) => (
                <FilterChip
                  key={cat.value}
                  label={cat.label}
                  active={filters.category === cat.value}
                  onClick={() => update({ category: filters.category === cat.value ? "" : cat.value })}
                />
              ))}
            </div>
          </div>
        </FilterDropdown>

        {/* Date */}
        <FilterDropdown
          label={t('filterBar.date.label')}
          active={!!hasDateFilter}
          activeLabel={dateLabel}
          onClear={() => update({ datePreset: null, dateRange: undefined })}
          className="w-auto"
        >
          <div className="min-w-[300px] sm:min-w-[560px] space-y-3">
            <div>
              <p className="font-display text-sm font-semibold tracking-tight text-foreground flex items-center gap-2">
                <span className={panelAccentDot} />
                {t('filterBar.date.label')}
              </p>
              <p className="text-xs text-muted-foreground/80 mt-1">{t('filterBar.date.helperText')}</p>
            </div>

            {/* Presets */}
            <div className="flex flex-wrap gap-1.5">
              {DATE_PRESET_VALUES.map((value) => (
                <FilterChip
                  key={value}
                  label={datePresetLabels[value]}
                  active={!showCustomCalendar && filters.datePreset === value}
                  onClick={() => {
                    if (filters.datePreset === value) {
                      update({ datePreset: null, dateRange: undefined });
                    } else {
                      // Set preset and also set dateRange for calendar visual
                      const range = getDateRangeFromPreset(value);
                      setShowCustomCalendar(false);
                      update({
                        datePreset: value,
                        dateRange: range ? { from: range.start, to: range.end } : undefined,
                      });
                    }
                  }}
                />
              ))}
              <FilterChip
                label={t('filterBar.date.customCalendar')}
                active={showCustomCalendar || (!filters.datePreset && !!filters.dateRange?.from)}
                onClick={() => setShowCustomCalendar((v) => !v)}
              />
            </div>

            {/* Calendar — only shown when picking a custom range, avoids duplicating the preset shortcuts above */}
            {(showCustomCalendar || (!filters.datePreset && !!filters.dateRange?.from)) && (
              <div className="pt-2">
                <Calendar
                  mode="range"
                  selected={filters.dateRange}
                  onSelect={(range) =>
                    update({ dateRange: range, datePreset: null })
                  }
                  numberOfMonths={2}
                  locale={ptBR}
                  disabled={(date) => date < startOfToday()}
                  className="p-3 pointer-events-auto rounded-2xl border border-border/50 bg-secondary/20"
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/60">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 rounded-full text-xs hover:bg-secondary/70"
                onClick={() => {
                  setShowCustomCalendar(false);
                  update({ datePreset: null, dateRange: undefined });
                }}
              >
                {t('filterBar.date.clearAction')}
              </Button>
            </div>
          </div>
        </FilterDropdown>

        {/* Price */}
        <FilterDropdown
          label={t('filterBar.price.label')}
          active={hasPriceFilter}
          activeLabel={priceLabel}
          onClear={() => update({ priceMin: "", priceMax: "" })}
        >
          <div className="min-w-[240px] space-y-3">
            <p className="font-display text-sm font-semibold tracking-tight text-foreground flex items-center gap-2">
              <span className={panelAccentDot} />
              {t('filterBar.price.label')}
            </p>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder={t('filterBar.price.minPlaceholder')}
                value={filters.priceMin}
                onChange={(e) => update({ priceMin: e.target.value })}
                className="text-sm h-10 rounded-2xl bg-secondary/50 border-transparent px-3.5 shadow-none focus:bg-background focus:ring-1 focus:ring-border/80 focus-visible:ring-1 focus-visible:ring-border/80 transition-all"
              />
              <Input
                type="number"
                placeholder={t('filterBar.price.maxPlaceholder')}
                value={filters.priceMax}
                onChange={(e) => update({ priceMax: e.target.value })}
                className="text-sm h-10 rounded-2xl bg-secondary/50 border-transparent px-3.5 shadow-none focus:bg-background focus:ring-1 focus:ring-border/80 focus-visible:ring-1 focus-visible:ring-border/80 transition-all"
              />
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {[
                { label: t('filterBar.price.upTo50'), min: "", max: "50" },
                { label: t('filterBar.price.upTo100'), min: "", max: "100" },
                { label: t('filterBar.price.upTo200'), min: "", max: "200" },
                { label: t('filterBar.price.plus200'), min: "200", max: "" },
              ].map((p) => (
                <FilterChip
                  key={p.label}
                  label={p.label}
                  active={filters.priceMin === p.min && filters.priceMax === p.max}
                  onClick={() => {
                    if (filters.priceMin === p.min && filters.priceMax === p.max) {
                      update({ priceMin: "", priceMax: "" });
                    } else {
                      update({ priceMin: p.min, priceMax: p.max });
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </FilterDropdown>

        {/* Modality */}
        <FilterDropdown
          label={t('filterBar.modality.label')}
          active={filters.modality !== "all"}
          activeLabel={modalityLabel}
          onClear={() => update({ modality: "all" })}
        >
          <div className="min-w-[170px] space-y-1">
            <p className="font-display text-sm font-semibold tracking-tight text-foreground flex items-center gap-2 mb-2">
              <span className={panelAccentDot} />
              {t('filterBar.modality.heading')}
            </p>
            {([
              { value: "all", label: t('filterBar.modality.all') },
              { value: "presential", label: t('filterBar.modality.presential') },
              { value: "online", label: t('filterBar.modality.online') },
            ] as const).map((m) => (
              <button
                key={m.value}
                onClick={() => update({ modality: m.value })}
                className={cn(
                  "w-full text-left px-3.5 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all duration-150 ease-out active:scale-[0.98]",
                  filters.modality === m.value
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-secondary/70 text-foreground/80 hover:text-foreground"
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
        </FilterDropdown>

        {/* Divider */}
        <div className="h-4 w-px bg-border/60 shrink-0 hidden sm:block mx-1 my-auto" />

        <span className="text-xs font-medium text-muted-foreground/75 shrink-0 hidden sm:inline-flex items-center mr-0.5">
          {t('filterBar.sortByLabel')}
        </span>

        {/* Sort */}
        <FilterDropdown
          label={filters.sort === "relevance" ? t('filterBar.sort.relevanceLabel') : t('filterBar.sort.dateLabel')}
          active={filters.sort === "relevance"}
          activeLabel={filters.sort === "relevance" ? t('filterBar.sort.relevanceLabel') : undefined}
          onClear={filters.sort === "relevance" ? () => update({ sort: "date" }) : undefined}
        >
          <div className="min-w-[170px] space-y-1">
            <p className="font-display text-sm font-semibold tracking-tight text-foreground flex items-center gap-2 mb-2">
              <span className={panelAccentDot} />
              {t('filterBar.sortByLabel')}
            </p>
            {([
              { value: "date", label: t('filterBar.sort.dateOption'), icon: CalendarIcon },
              { value: "relevance", label: t('filterBar.sort.relevanceOption'), icon: Flame },
            ] as const).map((s) => (
              <button
                key={s.value}
                onClick={() => update({ sort: s.value })}
                className={cn(
                  "w-full text-left px-3.5 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all duration-150 ease-out flex items-center gap-2 active:scale-[0.98]",
                  filters.sort === s.value
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-secondary/70 text-foreground/80 hover:text-foreground"
                )}
              >
                <s.icon className="h-3.5 w-3.5 opacity-70" />
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        </FilterDropdown>
      </div>

      {/* Active filters summary - mobile friendly */}
      {activeCount > 0 && (
        <div className="flex items-center gap-2 flex-wrap pt-0.5">
          <span className="text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-secondary/80 text-muted-foreground">
            {t('filterBar.activeCount', { count: activeCount })}
          </span>
          <button
            onClick={() => onChange({ ...defaultEventFilters })}
            className="text-xs font-medium text-primary hover:underline transition-colors ml-1"
          >
            {t('filterBar.clearAllAction')}
          </button>
        </div>
      )}
    </div>
  );
}
